import { useToast, type UseToastOptions } from '@chakra-ui/react';
import { useCallback, useMemo } from 'react';

type ToastStatus = 'success' | 'error' | 'warning' | 'info';

interface ToastOptions {
  description?: string;
  duration?: number;
}

const DEFAULT_DURATION: Record<ToastStatus, number> = {
  success: 2000,
  error: 3000,
  warning: 4000,
  info: 3000,
};

export const useMarketplaceToast = () => {
  const toast = useToast();

  const showToast = useCallback(
    (title: string, status: ToastStatus, options?: ToastOptions) => {
      const toastOptions: UseToastOptions = {
        title,
        status,
        duration: options?.duration ?? DEFAULT_DURATION[status],
        isClosable: true,
        position: 'top-right',
      };

      if (options?.description) {
        toastOptions.description = options.description;
      }

      toast(toastOptions);
    },
    [toast],
  );

  const success = useCallback(
    (title: string, options?: ToastOptions) =>
      showToast(title, 'success', options),
    [showToast],
  );

  const error = useCallback(
    (title: string, options?: ToastOptions) =>
      showToast(title, 'error', options),
    [showToast],
  );

  const warning = useCallback(
    (title: string, options?: ToastOptions) =>
      showToast(title, 'warning', options),
    [showToast],
  );

  const info = useCallback(
    (title: string, options?: ToastOptions) =>
      showToast(title, 'info', options),
    [showToast],
  );

  return useMemo(
    () => ({ success, error, warning, info, toast: showToast }),
    [success, error, warning, info, showToast],
  );
};
